// bedrockClient.js — v2.1  (Llama-3 manual JSON tool calls)
// =============================================================================
// 12-May-2025  • Patch 01
//   • Stops JSON tool-call blob leaking to chat UI
//   • Uses one UUID for tool_input + tool_result ⇒ no “orphaned tool” warnings
//   • Filters tool messages out of follow-up history to avoid $unknown SDK bug
//
// ──────────────────────────────────────────────────────────────────────────────
// ➊  The SYSTEM prompt must explain the tool and instruct the model:
//
//     { "tool":"plot_graph", "arguments":{ … } }
//
// ➋  Client flow
//     • Stream chunks → buffer in textBuffer
//     • Detect valid JSON tool call BEFORE emitting chunk
//     • On detection → executeToolCall → persist tool_input & tool_result
//       (same UUID) → start a new stream so the model can continue.
//
// ➌  describeImage() and summariseTextWithHaiku() are copied verbatim from
//     v1 — keep them below this section unchanged.
// =============================================================================

// -----------------------------------------------------------------------------
// Imports
// -----------------------------------------------------------------------------
import { executeToolCall } from './toolUtils';
import {
  ConverseStreamCommand,
  ConverseCommand
} from '@aws-sdk/client-bedrock-runtime';
import { getBedrockClient as getBedrockClientFromAWS } from './awsClients';
import {
  maybeSummarise,
  formatDBMessagesForBedrock,
  HARD_MESSAGE_LIMIT
} from './bedrockMessageHistoryUtils';
import { updateUserSpending } from './TokenUtils';
import { TOOL_CONFIG } from './toolConfig';
import { CHAT_LIMITS } from './usageChecker';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------
const CHAT_MODEL_ID         = 'arn:aws:bedrock:us-west-2:477591778252:inference-profile/us.meta.llama3-3-70b-instruct-v1:0';
const SUMMARISE_MESSAGES_ID = 'anthropic.claude-3-5-haiku-20241022-v1:0';
const VISION_MODEL_ID       = 'arn:aws:bedrock:us-west-2:477591778252:inference-profile/us.meta.llama4-maverick-17b-instruct-v1:0';
// const CHAT_MODEL_ID = `arn:aws:bedrock:us-west-2:477591778252:inference-profile/us.amazon.nova-pro-v1:0`;
const USER_SOLVING_PROBLEM_MODEL_ID = 'anthropic.claude-3-5-haiku-20241022-v1:0';

// -----------------------------------------------------------------------------
// Usage Limits
// -----------------------------------------------------------------------------

// Global cache to store user usage data (to minimize DB checks)
const usageCache = {
  data: {}, // userId -> { weekly_spend, monthly_spend, lastChecked, exceeded }
  
  // Check if usage exceeds limits
  isExceeded(userId) {
    const entry = this.data[userId];
    if (!entry) return false;
    
    // Cache expires after 5 minutes
    if (Date.now() - entry.lastChecked > 5 * 60 * 1000) {
      delete this.data[userId];
      return false;
    }
    
    return entry.exceeded;
  },
  
  // Update cache with new usage data
  update(userId, weekly_spend, monthly_spend) {
    const exceeded = (weekly_spend > CHAT_LIMITS.WEEKLY ||
                     monthly_spend > CHAT_LIMITS.MONTHLY);
    
    this.data[userId] = {
      weekly_spend,
      monthly_spend,
      lastChecked: Date.now(),
      exceeded,
      limitType: weekly_spend > CHAT_LIMITS.WEEKLY ? 'weekly' : 'monthly'
    };
    
    return exceeded;
  }
};

// -----------------------------------------------------------------------------
// Helper — detect `{ "tool":…, "arguments":… }` in a buffer
// -----------------------------------------------------------------------------
function tryExtractToolCall(buffer) {
  const openIdx  = buffer.indexOf('{');
  const closeIdx = buffer.lastIndexOf('}');
  if (openIdx === -1 || closeIdx === -1 || closeIdx <= openIdx) return null;

  const candidate = buffer
    .slice(openIdx, closeIdx + 1)
    .replace(/```json|```/gi, '')
    .trim();

  try {
    if (!candidate.startsWith('{')) return null;
    const obj = JSON.parse(candidate);
    if (obj &&
        typeof obj.tool === 'string' &&
        typeof obj.arguments === 'object') {
      return { call: obj, span: [openIdx, closeIdx + 1] };
    }
  } catch { /* not yet complete JSON */ }
  return null;
}

// -----------------------------------------------------------------------------
// Helper — Generate UUID
// -----------------------------------------------------------------------------
/**
 * Generate a random UUID that works across all browsers
 * Fallback for browsers that don't support crypto.randomUUID()
 */
function generateUUID() {
  // Use crypto.randomUUID if available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // Fallback implementation using Math.random()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// -----------------------------------------------------------------------------
// BrowserBedrockClient
// -----------------------------------------------------------------------------
export class BrowserBedrockClient {
  constructor(cfg = {}) {
    this.client      = cfg.client  || null;
    this.region      = cfg.region  || 'us-west-2';
    this.modelId     = cfg.modelId || CHAT_MODEL_ID;
    this.dynamoUtilsRef = cfg.dynamoUtilsRef || null;
    this.session       = cfg.session || null;
    this.initialized = !!this.client;
    this.defaultIncludeTools = /meta\.llama3-3-70b-instruct-v1/i.test(this.modelId) ? true : true;
    
    // Rate limiting properties to prevent infinite loops
    this.apiCalls = [];
  }
  
  // Simple check without await that throws on excess usage
  checkUsageLimits(userId) {
    if (!userId) return;
    
    // First check cache to see if we already know limits are exceeded
    if (usageCache.isExceeded(userId)) {
      const entry = usageCache.data[userId];
      const limitType = entry.limitType || 'monthly';
      const limitAmount = CHAT_LIMITS[limitType.toUpperCase()];
      throw new Error(`Usage limit exceeded: You have reached your ${limitType} usage limit. Please contact support for more information.`);
    }
    
    // Get dynamo client
    const dynamoUtils = this.dynamoUtilsRef?.current;
    if (!dynamoUtils) return;
    
    // Start a background check (no await)
    this.checkUsageBackground(userId, dynamoUtils);
  }
  
  // Background check that updates cache
  checkUsageBackground(userId, dynamoUtils) {
    // Skip if we don't have what we need
    if (!userId || !dynamoUtils) return;
    
    // Run in background
    (async () => {
      try {
        const userProfile = await dynamoUtils.getUserProfile(userId);
        if (!userProfile || !userProfile.spending) return;
        
        const { weekly_spend, monthly_spend } = userProfile.spending;
        
        // Update cache and check if exceeded
        const exceeded = usageCache.update(userId, weekly_spend, monthly_spend);
        
        if (exceeded) {
          console.warn(`[BrowserBedrockClient] User ${userId} has exceeded usage limits. W: $${weekly_spend.toFixed(2)}/$${CHAT_LIMITS.WEEKLY.toFixed(2)}, M: $${monthly_spend.toFixed(2)}/$${CHAT_LIMITS.MONTHLY.toFixed(2)}`);
          
          // If we just discovered a limit was exceeded, add a message to any active conversation
          const sessionId = this.session?.sessionId;
          if (sessionId && dynamoUtils) {
            const limitType = usageCache.data[userId].limitType || 'monthly';
            const limitAmount = CHAT_LIMITS[limitType.toUpperCase()];
            const message = `Usage limit exceeded: You have reached your ${limitType} token limit of $${limitAmount.toFixed(2)}. Please contact support for more information.`;
            
            await dynamoUtils.addMessage({
              conversationId: sessionId,
              userId: userId,
              messageType: 'text',
              role: 'system',
              content: message
            }).catch(err => console.error('Failed to add limit message to conversation:', err));
          }
        }
      } catch (error) {
        console.error('Error checking usage in background:', error);
      }
    })();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Initialise Bedrock client
  // ────────────────────────────────────────────────────────────────────────────
  async initialize() {
    if (this.initialized && this.client) return this.client;
    this.client      = await getBedrockClientFromAWS();
    this.initialized = true;
    if (!this.client) throw new Error('No Bedrock client available. Please log in.');
    return this.client;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // STREAM CHAT  (core method)
  // ────────────────────────────────────────────────────────────────────────────
  /**
   * @param {string} systemPrompt
   * @param {object} options { onChunk, onComplete, onError, toolContext }
   */
  async streamChat(systemPrompt, options = {}) {
    const includeToolMessages = options.includeToolMessages || this.defaultIncludeTools;
    await this.initialize();

    // default no-ops
    const onChunk     = options.onChunk    || (() => {});
    const onComplete  = options.onComplete || (() => {});
    const onError     = options.onError    || console.error;
    const toolContext = options.toolContext || {};

    try {
      // Check usage limits first - throws on excess
      if (toolContext.userId) {
        try {
          this.checkUsageLimits(toolContext.userId);
        } catch (error) {
          onError(error);
          
          // Also add to conversation if possible
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils && toolContext.sessionId && toolContext.userId) {
            await dynamoUtils.addMessage({
              conversationId: toolContext.sessionId,
              userId: toolContext.userId,
              messageType: 'text',
              role: 'system',
              content: error.message
            }).catch(err => console.error('Failed to add error message:', err));
          }
          
          return;
        }
      }

      // 1️⃣  Pull history + maybe summarise
      const dynamoUtils = this.dynamoUtilsRef?.current;
      let history = [];

      if (dynamoUtils && toolContext.sessionId && toolContext.userId) {
        history = await dynamoUtils.queryConversations(
          toolContext.sessionId, toolContext.userId, HARD_MESSAGE_LIMIT
        );

        if (history.length) {
          await maybeSummarise({
            conversationId: toolContext.sessionId,
            userId:         toolContext.userId,
            history,
            dynamoUtils,
            bedrockClient:  this
          });
          history = await dynamoUtils.queryConversations(
            toolContext.sessionId, toolContext.userId, HARD_MESSAGE_LIMIT
          );
        }
      }

      const messages = await formatDBMessagesForBedrock(
        history,
        { includeToolMessages, includeSystem: true }
      );

      // 2️⃣  Kick off ConverseStream (no toolConfig)
      // log conver input
      console.log('=== BEDROCK STREAMING SESSION STARTED ===');
      console.log('Messages:', messages);
      const conversation = await this.client.send(
        new ConverseStreamCommand({
          modelId: CHAT_MODEL_ID,
          system : [{ text: systemPrompt }],
          messages,
          inferenceConfig: { maxTokens: 4000, temperature: 0.1, topP: 0.9 }
        })
      );

      // 3️⃣  Stream loop
      let textBuffer       = '';
      let holdBuffer       = '';
      let rawAssistantText = '';
      let finalUsage       = null;
      let toolHandled      = false;
      let capturingTool    = false;
      
      console.log('=== BEDROCK STREAMING SESSION STARTED ===');

      for await (const event of conversation.stream) {
        // ── TEXT CHUNK ────────────────────────────────────────────────────────
        if (event.contentBlockDelta?.delta?.text) {
          const chunk = event.contentBlockDelta.delta.text;
          textBuffer += chunk;
          holdBuffer += chunk;
          
          if (!capturingTool && chunk.includes('{')) {
            // start capturing tool call
            capturingTool = true;
          }

          //------------------------------------------------------------------
          // 1️⃣  Detect complete tool-call (only if we're currently capturing)
          //------------------------------------------------------------------
          if (capturingTool && !toolHandled) {
            const found = tryExtractToolCall(textBuffer);
            
            // Check if we have a closing brace and should release the content
            if (!found && textBuffer.includes('}')) {
              // If we have a closing brace but no valid tool call (no "tool" property)
              // and the capture buffer doesn't contain '"tool":', release the content
              if (!textBuffer.includes('tool')) {
                capturingTool = false; // stop capturing
                rawAssistantText += textBuffer; // add captured content to assistant text
                onChunk({ type: 'textChunk', content: textBuffer }); // emit to user
                textBuffer = ''; // clear buffer after sending
              }
            } else if (found) {
              toolHandled = true;
              capturingTool = false; // stop capturing tool call
              // remove the JSON from the assistant text we keep
              rawAssistantText = rawAssistantText.replace(textBuffer.slice(...found.span), '');
              holdBuffer = '';
              await this.#handleToolCall(
                found.call,
                systemPrompt,
                { ...options, toolContext }
              );
              return;   // follow-up stream manages remainder
            }
          }

          //------------------------------------------------------------------
          // 2️⃣  If we're *not* in capture mode, the chunk is safe to show
          //------------------------------------------------------------------
          if (!capturingTool) {
            // flush the buffer
            rawAssistantText += textBuffer;
            onChunk({ type: 'textChunk', content: textBuffer });
            textBuffer = '';
          }
        }
        // ── METADATA / TOKEN USAGE ───────────────────────────────────────────
        if (event.metadata?.usage) finalUsage = event.metadata.usage;
      }

      // 4️⃣  Turn ended without tool use
      await this.#persistAssistantText(rawAssistantText, toolContext);
      await this.#updateSpending(finalUsage, toolContext);

      onComplete([
        ...messages,
        { role: 'assistant', content: [{ type: 'text', text: rawAssistantText }] }
      ]);

    } catch (err) {
      console.error('Error streaming chat:', err);
      onError(err);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // PRIVATE HELPERS
  // ────────────────────────────────────────────────────────────────────────────
  async #persistAssistantText(text, ctx) {
    if (!text || !ctx?.sessionId || !ctx?.userId) return;
    const dynamoUtils = this.dynamoUtilsRef?.current;
    if (!dynamoUtils) return;

    await dynamoUtils.addMessage({
      conversationId: ctx.sessionId,
      userId:         ctx.userId,
      messageType:    'text',
      role:           'assistant',
      content:        text
    });
  }

  async #updateSpending(usage, ctx) {
    if (!usage || !ctx?.userId) return;
    const dynamoUtils = this.dynamoUtilsRef?.current;
    if (!dynamoUtils) return;

    await updateUserSpending(usage, ctx.userId, CHAT_MODEL_ID, dynamoUtils, "Tutor Chat");
    
    // Trigger a background check to refresh usage cache after spending has been updated
    this.checkUsageBackground(ctx.userId, dynamoUtils);
  }

  /**
   * Executes the tool, records input & result, and spawns a follow-up stream.
   * @param {object} call    e.g. { tool:"plot_graph", arguments:{…} }
   * @param {string} systemPrompt  original system prompt
   * @param {object} options       original options
   */
  async #handleToolCall(call, systemPrompt, options) {
    const { tool: name, arguments: args } = call;
    const { onChunk, toolContext } = options;

    // 0️⃣  Shared UUID
    const toolUseId = generateUUID();

    // 1️⃣  Persist tool_input
    await this.#saveToolInput(toolUseId, name, args, toolContext);
    await this.#persistAssistantText(
      `Plotting graph: ${name}(${JSON.stringify(args)})`, toolContext);
    onChunk?.({ type: 'toolStart', toolName: name });

    // 2️⃣  Run tool
    let result;
    try {
      result = await executeToolCall(name, args, toolContext);
    } catch (err) {
      onChunk?.({ type: 'toolError', toolName: name, error: err.message });
      return;
    }

    // 3️⃣  Persist tool_result
    await this.#saveToolResult(toolUseId, name, result, toolContext);
    onChunk?.({ type: 'toolResult', toolName: name, result });

    // 4️⃣  Resume conversation with updated history
    const dynamoUtils = this.dynamoUtilsRef?.current;
    const fresh = dynamoUtils
      ? await dynamoUtils.queryConversations(
          toolContext.sessionId,
          toolContext.userId,
          HARD_MESSAGE_LIMIT
        )
      : [];

    const nextClient = new BrowserBedrockClient({
      client: this.client,
      region: this.region,
      modelId: this.modelId
    });
    await nextClient.streamChat(
      systemPrompt,
      { ...options, toolContext, includeToolMessages: true});
  }

  async #saveToolInput(id, name, args, ctx) {
    const dynamoUtils = this.dynamoUtilsRef?.current;
    if (!dynamoUtils || !ctx?.sessionId || !ctx?.userId) return;

    // Save the complete tool call structure without the outer array wrapper
    const toolCallJson = { tool: name, arguments: args };
    
    await dynamoUtils.addMessage({
      conversationId: ctx.sessionId,
      userId:         ctx.userId,
      messageType:    'tool_input',
      role:           'assistant',
      // Format that will be presented to the LLM in the history - without outer array
      content: JSON.stringify({
        toolUse: { toolUseId: id, name, input: args }
      }),
      // Save the raw tool call for reference
      toolInfo: { 
        id, 
        name, 
        input: args,
        rawToolCall: toolCallJson 
      }
    });
  }

  async #saveToolResult(id, name, result, ctx) {
    const dynamoUtils = this.dynamoUtilsRef?.current;
    if (!dynamoUtils || !ctx?.sessionId || !ctx?.userId) return;

    // Format the tool result without the outer array as well
    await dynamoUtils.addMessage({
      conversationId: ctx.sessionId,
      userId:         ctx.userId,
      messageType:    'tool_result',
      role:           'user',
      // Format that will be presented to the LLM in the history - without outer array
      content: JSON.stringify({
        toolResult: {
          toolUseId: id,
          content: [{ type: 'json', json: result }]
        }
      }),
      // Save additional reference information
      toolInfo: { 
        id, 
        name, 
        result,
        rawToolResult: result
      }
    });
  }

  // ---------------------------------------------------------------------------
  // GENERATE SUGGESTED RESPONSES
  // ---------------------------------------------------------------------------
  /**
   * Generates suggested next responses based on recent conversation context
   * @param {Array} recentMessages - Recent messages from the conversation
   * @param {string} userId - User ID for token tracking
   * @returns {Array} - Array of suggested response strings
   */
  async generateSuggestedResponses(recentMessages, userId = null) {
    await this.initialize();
    
    // Check usage limits - return empty array if exceeded
    try {
      if (userId) {
        this.checkUsageLimits(userId);
      }
    } catch (error) {
      console.warn(`Cannot generate suggested responses: ${error.message}`);
      return [];
    }
    
    // Take only the last few messages to keep context concise
    const limitedMessages = recentMessages.slice(-4);
    
    // Format the messages for the prompt
    const conversationText = limitedMessages.map(msg => {
      const role = msg.role.toUpperCase();
      let content = '';
      
      if (typeof msg.content === 'string') {
        content = msg.content;
      } else if (Array.isArray(msg.content)) {
        content = msg.content
          .filter(item => item.type === 'text')
          .map(item => item.text)
          .join('\n');
      }
      
      return `${role}: ${content}`;
    }).join('\n\n');

    const suggestionsPrompt = `You're helping generate suggested responses for a student in a math tutoring session.

CONVERSATION CONTEXT:
${conversationText}

TASK:
Generate 3 short suggested responses (5-15 words each) the student could send next.

GUIDELINES:
- Responses should be helpful continuations of the conversation
- NEVER suggest actual answers to math problems
- Focus on asking for clarification, examples, or different explanations
- Make responses conversational and natural
- Each suggestion should serve a different purpose

EXAMPLE PATTERNS:
- "Can you explain that step by step?"
- "I'm still confused about..."
- "Can you show me another example?"
- "How does this relate to [relevant topic]?"
- "What's the formula for this?"
- "Could you draw/graph this for me?"
- "Why is this approach better?"
- "Can you explain this in simpler terms?"
- "How would I apply this to..."
- "Let me try solving the next one"

Return ONLY valid JSON in this format:
{"suggestions": ["suggestion1", "suggestion2", "suggestion3"]}`;

    try {      
      const converseInput = {
        modelId: CHAT_MODEL_ID,
        system: [{ 
          text: "You are a helpful assistant that generates short, relevant suggested responses for students in a math tutoring session. Return only JSON." 
        }],
        messages: [{ 
          role: 'user', 
          content: [{ type: 'text', text: suggestionsPrompt }] 
        }],
        inferenceConfig: { 
          maxTokens: 200, 
          temperature: 0.2,
        }
      };
      
      console.log('Requesting suggested responses');
      const response = await this.client.send(new ConverseCommand(converseInput));
      
      // Track token usage
      if (userId) {
        try {
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils) {
            await updateUserSpending(
              response.usage,
              userId,
              CHAT_MODEL_ID,
              dynamoUtils,
              "Suggested Responses"
            );
          } else {
            console.warn('No dynamoUtils available for token usage tracking');
          }
        } catch (error) {
          console.warn('Error tracking token usage for suggestions:', error);
        }
      }
      
      // Extract and parse JSON response
      const responseText = response.output.message.content[0].text;
      try {
        // Find JSON pattern in response
        const jsonMatch = responseText.match(/{[\s\S]*}/);
        if (jsonMatch) {
          const parsedData = JSON.parse(jsonMatch[0]);
          return parsedData.suggestions || [];
        }
        return [];
      } catch (parseError) {
        console.error('Error parsing suggestions JSON:', parseError);
        return [];
      }
    } catch (error) {
      console.error('Error generating suggested responses:', error);
      return [];
    }
  }

  /**
   * Process an image using Bedrock (Claude 3 Haiku Vision) to get a description.
   * @param {string} imageUrl - The HTTPS URL of the image to describe.
   * @returns {Promise<string>} - The description text from the model.
   */
  async describeImage(imageUrl, userId) {
    await this.initialize();
    if (!this.client) {
      throw new Error("Bedrock client not initialized.");
    }
    
    // Check usage limits first - return error message if exceeded
    try {
      if (userId) {
        this.checkUsageLimits(userId);
      }
    } catch (error) {
      return error.message;
    }
    
    // Call Bedrock to describe the image with focus on text extraction
    const visionPrompt = `You are an expert at understanding images related to mathematics, particularly student sketches, diagrams, screenshots of problemns, graphs and math related text, etc. A user has provided this images as part of a math tutoring session, and you are to describe the image in detail. Describe what you see including any text, math equations, graphs, diagrams, or mathematical expressions. If the user has uploaded a plot, graph, or diagram, describe it in detail, including any labels, axes, and key features. Just return your detailed description and nothing else.`;

    try {
      // Log start time and parameters
      console.time('📊 Image Description Processing');
      console.log('%c🔍 Starting image description with Vision API', 'color: #4a6fa5; font-weight: bold;');
      console.log('📷 Image URL:', imageUrl);
      console.log('🤖 Using model:', VISION_MODEL_ID);

      // Fetch the image data from the URL
      console.time('⬇️ Image Fetch Time');
      const response = await fetch(imageUrl);
      console.timeEnd('⬇️ Image Fetch Time');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      const imageBlob = await response.blob();
      console.log('📏 Image size:', Math.round(imageBlob.size / 1024), 'KB');
      console.log('🖼️ Image type:', imageBlob.type);

      // Log image dimensions
      const imgUrl = URL.createObjectURL(imageBlob);
      const img = new Image();
      img.onload = () => {
        console.log('🖼️ Actual image dimensions:', `${img.width}x${img.height} pixels`);
        URL.revokeObjectURL(imgUrl);
      };
      img.src = imgUrl;

      // Convert Blob to base64
      console.time('🔄 Base64 Conversion');
      const reader = new FileReader();
      const base64Image = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imageBlob);
      });
      console.timeEnd('🔄 Base64 Conversion');
      
      // Get the image media type, making sure it's one of the supported formats
      // Note: if this was a converted HEIC, it should now be a PNG
      const imageMediaType = imageBlob.type || 'image/png';
      const format = imageMediaType.split('/')[1].toLowerCase(); // e.g., 'png', 'jpeg'
      
      // Ensure the format is supported by Bedrock API
      const supportedFormats = ['png', 'jpeg', 'jpg', 'gif'];
      const bedrockFormat = supportedFormats.includes(format) ? format : 'png';

      // Construct the message for Bedrock Converse API
      const converseInput = {
        modelId: VISION_MODEL_ID,
        messages: [
          {
            role: 'user',
            content: [
              {
                image: {
                  format: bedrockFormat, 
                  source: {
                    bytes: Buffer.from(base64Image, 'base64')
                  }
                }
              },
              { text: visionPrompt } // Add the prompt text here
            ]
          }
        ],
        inferenceConfig: {
          maxTokens: 4000, // Increased from 128 to get more detailed descriptions
          temperature: 0.1, // As specified
        },
        // No system prompt needed as it's included in the user message content
      };

      console.log('🔮 Sending request to Bedrock API...', converseInput);
      console.time('⏱️ Bedrock API Response Time');
      
      // Use the non-streaming ConverseCommand
      const command = new ConverseCommand(converseInput);
      const bedrockResponse = await this.client.send(command);
      console.timeEnd('⏱️ Bedrock API Response Time');

      console.log('%c📥 Received Bedrock response!', 'color: #4a9f5a; font-weight: bold;');
      
      // Extract the text content from the response
      let description = '';
      if (bedrockResponse.output?.message?.content?.[0]?.text) {
        description = bedrockResponse.output.message.content[0].text;
        
        // Log detailed stats about the response
        console.group('📊 Image Description Statistics');
        console.log('📝 Description length:', description.length, 'characters');
        console.log('🔤 Word count:', description.split(/\s+/).length, 'words');
        console.log('📋 Paragraph count:', description.split(/\n\s*\n/).length);
        
        // Log token usage if available
        if (bedrockResponse.usage) {
          console.log('🔢 Input tokens:', bedrockResponse.usage.inputTokens);
          console.log('🔢 Output tokens:', bedrockResponse.usage.outputTokens);
          console.log('🔢 Total tokens:', bedrockResponse.usage.totalTokens);

          // Track token usage for cost calculation if available
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils) {
            console.log('Updating user spending with image description token usage:', bedrockResponse.usage);
            await updateUserSpending(
              bedrockResponse.usage,
              userId,
              VISION_MODEL_ID,
              dynamoUtils,
              "Image"
            );
            console.log('User spending updated successfully for image description');
          }
        }
        
        console.groupEnd();
        
        // Log a sample of the description
        const sample = description.length > 200 
          ? description.substring(0, 200) + '...' 
          : description;
        console.log('%c📄 Sample of description:', 'font-style: italic;', sample);
        
        console.timeEnd('📊 Image Description Processing');
        return description;
      } else {
        console.error('❌ No description text found in Bedrock response!', bedrockResponse);
        throw new Error("No description text found in Bedrock response.");
      }

    } catch (error) {
      console.error('❌ Error describing image with Bedrock:', error);
      console.timeEnd('📊 Image Description Processing');
      throw new Error(`Failed to get image description: ${error.message}`);
    }
  }
  
  /**
   * Summarise arbitrary text using Claude 3 Haiku (cheap + fast).
   */
  async summariseTextWithHaiku(text) {
    await this.initialize();
    
    // Check usage limits - return error message if exceeded
    try {
      // Try to get the user ID from global context
      const userId = this.session?.userId;
      if (userId) {
        this.checkUsageLimits(userId);
      }
    } catch (error) {
      return `ERROR: ${error.message}`;
    }
    
    const converseInput = {
      modelId: SUMMARISE_MESSAGES_ID,
      system: [{ text: "You are an expert summariser. You take conversations between an AI and a user and you summarise the conversation so far. Start your response with 'Conversation summary:' and then provide a summary of the conversation so far. Do not include any other text. If the snippet includes the initial conversation lesson plan, make sure to summarise that and retain as much detail as possible such as the chapter, difficulty, textbook, key ideas, lesson plan and review. Otherwise, focus on collecting key information in the interaction such as user learning/process, outcomes covered, and any other notes that would be useful context." }],
      messages: [ { role:'user', content:[{ type:'text', text }] } ],
      inferenceConfig: { maxTokens: 800, temperature: 0.2 }
      
    };
    const response = await this.client.send(new ConverseCommand(converseInput));
    console.log('Haiku summary response:', response.output);
    
    // Track token usage for summarization
    if (response.usage) {
      try {
        // Try to get the user ID from global context
        const userId = this.session?.userId;
        if (userId) {
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils) {
            console.log('Updating user spending with text summarization token usage:', response.usage);
            await updateUserSpending(
              response.usage,
              userId,
              SUMMARISE_MESSAGES_ID,
              dynamoUtils,
              "Summary"
            );
            console.log('User spending updated successfully for text summarization');
          }
        }
      } catch (spendingError) {
        console.error('Error updating user spending for text summarization:', spendingError);
        // Non-critical error, continue with the response
      }
    }
    
    return response.output?.message?.content?.[0]?.text || '(empty summary)';
  }

  /**
   * Determine if the user has provided a solution or math in an attempt to solve a problem.
   * We need to know this so as to know if we should verify the accuracy of the solution or attempt.
   * @param {string} assistantText - The last assistant message.
   * @param {string} userText - The last user message.
   * @param {string} userId - The user ID for token usage tracking
   * @returns {Promise<boolean>} - True if the user is solving a problem, false otherwise.
   */
  async determineIfUserSolvingProblem(assistantText, userText, userId = null) {
    await this.initialize();
    
    // Check usage limits - return null if exceeded
    try {
      if (userId) {
        this.checkUsageLimits(userId);
      }
    } catch (error) {
      return error.message;
    }
    
    const solving_problem_prompt = `Here is a conversation between an AI Maths Tutor and a user. Determine if the user's message contains any of the following:
    1. A numerical answer (e.g., "2", "-5", "1/2", "0.5")
    2. A mathematical expression or equation
    3. A step-by-step solution or explanation of mathematical work
    4. A verbal description of mathematical operations (e.g., "you add them together", "multiply both sides")
    5. Any mathematical reasoning or logic, even if expressed in plain language
    6. Variables or mathematical symbols (x, y, π, =, etc.)
    7. A guess or attempt at solving a mathematical problem
    
    AI Tutor: ${assistantText}
    
    User: ${userText}
    
    Based on these criteria, is this a response that needs mathematical verification? Answer only with true or false.`;

    try {
      const converseInput = {
        modelId: USER_SOLVING_PROBLEM_MODEL_ID,
        system: [{ text: "You are an expert mathematician who determines if the user is currently trying to solve a problem and is providing an answer that needs verifying. The conversation is between an AI Maths Tutor and a user, and you will be provided with the last two messages from the conversation. If the user is solving a problem, return true, if not, return false." }],
        messages: [{ role: 'user', content: [{ type: 'text', text: solving_problem_prompt }] }],
        inferenceConfig: { maxTokens: 5, temperature: 0.0 }
      };
      
      console.log('Sending request to determine if user is solving a problem');
      const response = await this.client.send(new ConverseCommand(converseInput));
      console.log('User solving problem response:', response.output);
      
      // Track token usage - use global context userId if not provided
      const effectiveUserId = userId || this.session?.userId;
      if (response.usage && effectiveUserId) {
        try {
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils) {
            console.log('Updating user spending for problem-solving detection:', response.usage);
            await updateUserSpending(
              response.usage,
              effectiveUserId,
              USER_SOLVING_PROBLEM_MODEL_ID,
              dynamoUtils,
              "Determine Solving Problem"
            );
            console.log('User spending updated successfully for problem-solving detection');
          } else {
            console.warn('No dynamoUtils available for token usage tracking');
          }
        } catch (spendingError) {
          console.error('Error updating user spending for problem-solving detection:', spendingError);
          // Non-critical error, continue with the response
        }
      } else {
        console.warn('Missing userId or usage data for token tracking', { 
          hasUsage: !!response.usage, 
          userId: effectiveUserId 
        });
      }
      
      // Get response text and normalize it
      const responseText = response.output?.message?.content?.[0]?.text || '';
      const normalizedResponse = responseText.toLowerCase().trim();
      
      // Check if the response contains 'true'
      return normalizedResponse.includes('true');
    } catch (error) {
      console.error('Error in determineIfUserSolvingProblem:', error);
      return false; // Default to false on error
    }
  }

  /**
   * Take the last assistant and user message and analyse if the user has correctly solved the problem.
   * The model will look at the problem and the user's response step by step and determine if the user has solved the problem correctly.
   * The model will output "The user has solved the problem correctly" or "The user has not solved the problem correctly" and provide a detailed explanation of what the user did wrong. We will use the llama chat model for this.
   * @param {string} assistantText - The last assistant message.
   * @param {string} userText - The last user message.
   * @param {string} userId - The user ID for token usage tracking
   * @returns {Promise<string>} - Analysis of whether the user solved the problem correctly
   */
  async determineIfUserSolvedProblemCorrectly(assistantText, userText, userId = null) {
    await this.initialize();
    
    // Get effective user ID
    const effectiveUserId = userId || this.session?.userId;
    
    // Check usage limits - return error message if exceeded
    try {
      if (effectiveUserId) {
        this.checkUsageLimits(effectiveUserId);
      }
    } catch (error) {
      return error.message;
    }
    
    const solved_problem_correctly_prompt = `Here is a problem you need to verify the accuracy of. Be sure to check the users response step by step and determine if they have solved the problem correctly or if they have made any mistakes.
    
    AI Tutor: ${assistantText}
    
    User: ${userText}
    
    In your response, analyse the users answer and state "User's response was correct" if correct, or "Users response was incorrect" and explain what they got wrong and if they were close.`;

    try {
      const converseInput = {
        modelId: CHAT_MODEL_ID,
        system: [{ text: "You are an expert mathematician who determines if the user has solved the problem correctly. The conversation is between an AI Maths Tutor and a user, and you will be provided with the last two messages from the conversation. You will return if the user has solved the problem correct, and if not, an explanation of what they did wrong." }],
        messages: [{ role: 'user', content: [{ type: 'text', text: solved_problem_correctly_prompt }] }],
        inferenceConfig: { maxTokens: 500, temperature: 0.0 },
      };
      
      console.log('Sending request to determine if user solved problem correctly');
      const response = await this.client.send(new ConverseCommand(converseInput));
      console.log('User solution verification response received');
      
      // Track token usage - use global context userId if not provided
      const effectiveUserId = userId || this.session?.userId;
      if (response.usage && effectiveUserId) {
        try {
          const dynamoUtils = this.dynamoUtilsRef?.current;
          if (dynamoUtils) {
            console.log('Updating user spending for solution verification:', response.usage);
            await updateUserSpending(
              response.usage,
              effectiveUserId,
              CHAT_MODEL_ID,
              dynamoUtils,
              "Determine Solved Problem"
            );
            console.log('User spending updated successfully for solution verification');
          } else {
            console.warn('No dynamoUtils available for token usage tracking');
          }
        } catch (spendingError) {
          console.error('Error updating user spending for solution verification:', spendingError);
          // Non-critical error, continue with the response
        }
      } else {
        console.warn('Missing userId or usage data for token tracking in solution verification', { 
          hasUsage: !!response.usage, 
          userId: effectiveUserId 
        });
      }
      
      return response.output?.message?.content?.[0]?.text || '(empty analysis)';
    } catch (error) {
      console.error('Error in determineIfUserSolvedProblemCorrectly:', error);
      return 'Error verifying solution: ' + error.message;
    }
  }
}

/**
 * Factory function to create a Bedrock client
 */
export function getBedrockClient(config = {}) {
  return new BrowserBedrockClient(config);
}
